import React from "react"
import * as SwitchPrimitive from "@radix-ui/react-switch"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Switch - Toggle switch for boolean options
 * 
 * Używa Radix UI Switch primitive
 * - Rozmiary: "1" | "2" | "3"
 * - Variants: "classic" | "soft" | "surface"
 * - Animowana thumb transition
 * 
 * @example
 * ```tsx
 * <div className="flex items-center gap-2">
 *   <Switch 
 *     checked={enabled} 
 *     onCheckedChange={setEnabled}
 *     id="notifications"
 *   />
 *   <Label htmlFor="notifications">Powiadomienia</Label>
 * </div>
 * ```
 */

const switchVariants = cva(
  cn(
    "peer inline-flex shrink-0",
    "cursor-pointer items-center",
    "rounded-full border-2 border-transparent",
    "transition-colors duration-150",
    "focus-visible:outline-none focus-visible:ring-2",
    "focus-visible:ring-[var(--pink-9)] focus-visible:ring-offset-2",
    "focus-visible:ring-offset-[var(--dark-1)]",
    "disabled:cursor-not-allowed disabled:opacity-50",
    "data-[state=checked]:bg-[var(--pink-9)]",
    "data-[state=unchecked]:bg-[var(--dark-a5)]"
  ),
  {
    variants: {
      variant: {
        classic: "",
        soft: "data-[state=unchecked]:bg-[var(--gray-a4)]",
        surface: "data-[state=unchecked]:bg-[var(--dark-a3)]",
      },
      size: {
        "1": cn(
          "h-[20px] w-[36px]"
        ),
        "2": cn(
          "h-[24px] w-[44px]"
        ),
        "3": cn(
          "h-[28px] w-[52px]"
        ),
      },
    },
    defaultVariants: {
      variant: "classic",
      size: "2",
    },
  }
)

const switchThumbVariants = cva(
  cn(
    "pointer-events-none block rounded-full",
    "bg-white",
    "shadow-[0_2px_2px_rgba(0,0,0,0.2)]",
    "transition-transform duration-150",
    "data-[state=unchecked]:translate-x-0"
  ),
  {
    variants: {
      size: {
        "1": cn(
          "h-[16px] w-[16px]",
          "data-[state=checked]:translate-x-[16px]"
        ),
        "2": cn(
          "h-[20px] w-[20px]",
          "data-[state=checked]:translate-x-[20px]"
        ),
        "3": cn(
          "h-[24px] w-[24px]",
          "data-[state=checked]:translate-x-[24px]"
        ),
      },
    },
    defaultVariants: {
      size: "2",
    },
  }
)

export interface SwitchProps
  extends React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>,
    VariantProps<typeof switchVariants> {}

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(({ className, variant, size, ...props }, ref) => (
  <SwitchPrimitive.Root
    className={cn(switchVariants({ variant, size }), className)}
    {...props}
    ref={ref}
  >
    <SwitchPrimitive.Thumb
      className={cn(switchThumbVariants({ size }))}
    />
  </SwitchPrimitive.Root>
))
Switch.displayName = SwitchPrimitive.Root.displayName

export { Switch, switchVariants }

